import { window } from "../utils/window.util";
import { deiterateAPI } from "../utils/deiterateAPI.util";
import { sanitise } from "../utils/sanitise.util";
import { auth, db, FirebaseFieldValue, firebaseImport, FirebaseTimestamp, storageRef, storage, firebaseConfig } from "../../config/main_config";
import SupportingDocsModal from '../../../../htmlComponents/SupportingDocsModal.html?raw';
import { loadSelectorModal } from "../ControlSelector/controlSelector.module";
import { nameFromUID } from "../utils/nameFromUID.util";
import Swal from "sweetalert2";
import * as SparkMD5 from "spark-md5";
import { downloadFromSDK } from "../downloadFromSDK.module";
window.SparkMD5 = SparkMD5.default;

declare global {
    interface Date {
        addDays(days: number): Date;
    }

    interface String {
        toTitleCase(): string;
    }
}

// Implement the addDays method
Date.prototype.addDays = function (this: Date, days: number): Date {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
};

String.prototype.toTitleCase = function () {
    return this.replace(/\b\w/g, function (char: string) {
        return char.toUpperCase();
    })
        .replace(/([A-Z])/g, ' $1')
        .trim();
};


const formatHistoryValue = (value: any): string => {
    if (value instanceof FirebaseTimestamp) {
        return value.toDate().toLocaleDateString();
    }
    if (value instanceof firebaseImport.firestore.GeoPoint) {
        return `${value.latitude}, ${value.longitude}`;
    }
    if (value instanceof Date) {
        return value.toLocaleDateString();
    }
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }
    return value.toString();
};

/**
 * Decodes HTML entities in a given string.
 *
 * @param text - The encoded string containing HTML entities.
 * @returns The decoded string with HTML entities converted to their corresponding characters.
 */
function decodeHTMLEntities(text: string): string | null {
    const parser: DOMParser = new DOMParser();
    const doc: Document = parser.parseFromString(text, 'text/html');
    return doc.documentElement.textContent;
}


export namespace SupportingDocs {
    export enum DocType {
        Standard,
        Custom,
        Any,
    }

    export enum SupportedUpdateTypes {
        File,
        Confluence,
        EditOnly
    }

    export type CompanyID = string;
    export type DocumentReference = firebaseImport.firestore.DocumentReference;
    export type CollectionReference = firebaseImport.firestore.CollectionReference;

    export interface ConfluencePage {
        pageName: string;
        pageID: string;
        customerID: string;
        userID: string;
        lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        docClassification?: string | null;
        docComments?: string | null;
        docProcRef?: string[] | null;
        confluenceURL?: string | null;
        edit: boolean;
        docID?: string | null;

    }

    export interface IDocumentData {
        classification?: string | null;
        comments?: string | null;
        confluenceURL?: string | null;
        dateUploaded?: firebaseImport.firestore.Timestamp | Date | Object | null;
        docHash?: string | null;
        docID?: string | null;
        documentType?: string | null;
        fileName?: string | null;
        displayName?: string | null;
        lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        msgPages?: string[];
        nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        procRef?: string[] | null;
        uploadedBy?: string | null;
        updateMethod?: string | null;
        updatedBy?: string | null;
        companyID: CompanyID;
        documentHistory?: string[] | null;
        componentId?: string | null;
        status?: string | null;
        [key: string]: any;
    }

    export class Page {
        public injectScripts(scripts: string | string[], moduleName): void {
            if (typeof scripts === "string") {
                // Check if module exists on window already
                if (window[moduleName] !== undefined) {
                    console.log(`Module already loaded: ${moduleName}`);
                    return;
                }

                // Check if script already exists in the document
                const existingScript = Array.from(document.getElementsByTagName('script')).find(
                    (script) => script.src === scripts
                );
                if (existingScript) {
                    console.log(`Script already loaded: ${scripts}`);
                    return;
                }

                // Create a new script element and set its source attribute to the provided URL or array of URLs
                var script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = scripts;

                // Optional: Add onload event handler
                script.onload = function () {
                    console.log('Library has been loaded.', script);
                };

                // Optional: Add onerror event handler
                script.onerror = function () {
                    console.error('Failed to load library.', script);
                };

                // Append the script to the head or body
                document.head.appendChild(script);
            } else {
                scripts.forEach((scriptLink) => {
                    // Check if script already exists in the document
                    const existingScript = Array.from(document.getElementsByTagName('script')).find(
                        (script) => script.src === scriptLink
                    );
                    if (existingScript) {
                        console.log(`Script already loaded: ${scriptLink}`);
                        return;
                    }

                    // Create a new script element and set its source attribute to the provided URL or array of URLs
                    var script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.src = scriptLink;

                    // Optional: Add onload event handler
                    script.onload = function () {
                        console.log('Library has been loaded.', script);
                    };

                    // Optional: Add onerror event handler
                    script.onerror = function () {
                        console.error('Failed to load library.', script);
                    };

                    // Append the script to the head or body
                    document.head.appendChild(script);
                });
            }
        }
    }

    export class Documents {
        private _documents: Document[] = [];
        private _companyID: CompanyID;

        public get documents(): Document[] {
            return this._documents;
        }

        public constructor(companyID: CompanyID | null | undefined = null) {
            if (companyID === null || companyID === undefined) {
                this._companyID = window.companyID;
            } else {
                this._companyID = companyID;
            }
            // new Page().injectScripts("https://cdnjs.cloudflare.com/ajax/libs/spark-md5/3.0.2/spark-md5.min.js");
            this.loadDocuments(true);
        }

        /**
         * Loads documents from the API.
         */
        public async loadDocuments(fresh: boolean = false): Promise<Document[]> {
            if (this._documents.length === 0 || fresh) {
                try {
                    const res = await deiterateAPI.GET('/v1/documents', {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: {
                            'X-Tenancy-Id': this._companyID,
                            'Company-ID': this._companyID,
                        }
                    });
                    
                    if (res.docs && Array.isArray(res.docs)) {
                        this._documents = res.docs.map((docData: any) => {
                            return new Document({ ...docData, companyID: this._companyID });
                        });
                    } else {
                        this._documents = [];
                    }
                } catch (error) {
                    console.error("Error loading documents:", error);
                    this._documents = [];
                }
            }
            return this._documents;
        }

        /**
         * Fetches all deleted document revisions from the API
         * @param companyID Optional company ID, defaults to current company
         * @returns Promise that resolves to an array of deleted document revisions
         */
        public static async getDeletedDocuments(companyID: string | null = window.companyID || null): Promise<Array<{
            id: string;
            data: any;
            deletedAt: any;
            deletedBy: string;
            originalDocId: string;
            originalFileName: string;
        }>> {
            try {
                const effectiveCompanyID = companyID || window.companyID;
                if (!effectiveCompanyID) {
                    throw new Error("No company ID provided and window.companyID is not set");
                }

                console.log(`[SupportingDocs] Fetching deleted documents for company: ${effectiveCompanyID}`);

                const res = await deiterateAPI.GET('/v1/documents/deleted', {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: {
                        'X-Tenancy-Id': effectiveCompanyID,
                        'Company-ID': effectiveCompanyID,
                    }
                });

                if (!res.deletedDocs || res.deletedDocs.length === 0) {
                    console.log('[SupportingDocs] No deleted documents found');
                    return [];
                }

                console.log(`[SupportingDocs] Found ${res.deletedDocs.length} deleted documents`);
                return res.deletedDocs;
            } catch (error) {
                console.error('[SupportingDocs] Error fetching deleted documents:', error);
                throw new Error(`Failed to fetch deleted documents: ${error.message}`);
            }
        }
    }

    export class Document {
        public classification?: string | null;
        public comments?: string | null;
        public confluenceURL?: string | null;
        public dateUploaded?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public docHash?: string | null;
        public docID?: string | null;
        public fileName?: string | null;
        public displayName?: string | null;
        public lastReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public msgPages?: string[] = [];
        public nextReviewDate?: firebaseImport.firestore.Timestamp | Date | Object | null;
        public procRef?: string[] | null;
        public assignedUsers?: string[] | null;
        public uploadedBy?: string | null;
        public updateMethod?: string | null;
        public updatedBy?: string | null;
        public status?: string | null;
        public documentType?: string | null;
        public componentId?: string | null;
        public version?: string | null;
        public linkedFromTaskId?: string | null;
        public companyID: CompanyID;
        [key: string]: any;

        private _originalData: IDocumentData;

        constructor(documentData: IDocumentData | null = null) {
            if (documentData !== null) {
                Object.assign(this, documentData);
                // Map Firestore field names to Document class property names
                // API returns docClassification, docComments, docProcRefs but class uses classification, comments, procRef
                if ((documentData as any).docClassification && !this.classification) {
                    this.classification = (documentData as any).docClassification;
                }
                if ((documentData as any).docComments && !this.comments) {
                    this.comments = (documentData as any).docComments;
                }
                if ((documentData as any).docProcRefs && !this.procRef) {
                    this.procRef = (documentData as any).docProcRefs;
                }
                // Map userID to uploadedBy (Firestore uses userID, frontend uses uploadedBy)
                if ((documentData as any).userID && !this.uploadedBy) {
                    this.uploadedBy = (documentData as any).userID;
                }
                this._originalData = { ...documentData }; // Shallow copy for change detection
            } else {
                this._originalData = { companyID: (this.companyID === undefined ? window.companyID : this.companyID) };
            }
        }

        /**
         * Checks if the document has unsaved changes.
         */
        public get hasChanged(): boolean {
            return !this._isEqual(this._originalData, this.toData());
        }

        /**
         * Converts the document instance to a plain object suitable for Firestore.
         */
        public toData(): IDocumentData {
            return {
                assignedUsers: this.assignedUsers || [],
                classification: this.classification || 'Business Confidential',
                comments: this.comments || '',
                confluenceURL: this.confluenceURL || null,
                dateUploaded: this.dateUploaded || new Date(),
                docHash: this.docHash || '',
                docID: this.docID,
                fileName: this.fileName,
                displayName: this.displayName ?? null,
                lastReviewDate: this.lastReviewDate || new Date(),
                msgPages: this.msgPages || [],
                nextReviewDate: this.nextReviewDate || new Date(),
                procRef: this.procRef || [],
                uploadedBy: this.uploadedBy || auth.currentUser.uid || '',
                updateMethod: this.updateMethod || '',
                updatedBy: auth.currentUser.uid,
                companyID: (this.companyID === undefined ? window.companyID : this.companyID),
                documentType: this.documentType || "Policy",
                componentId: this.componentId || "SUP-" + this.docID,
                status: this.status || "Draft",
                version: this.version || '1.0',
                linkedFromTaskId: this.linkedFromTaskId || null
            };
        }

        /*
        * Checks if document has key
        */
        private _hasKey(key: string): boolean {
            return Object.prototype.hasOwnProperty.call(this, key);
        }

        /**
         * Updates the document via API.
         */
        public async update(): Promise<void> {
            const updatedData = this.toData();
            console.log(updatedData)
            delete updatedData?.companyID;
            try {
                const companyID = this.companyID === undefined ? window.companyID : this.companyID;
                
                await deiterateAPI.PUT(`/v1/documents/${this.docID}`, {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: {
                        'X-Tenancy-Id': companyID,
                        'Company-ID': companyID,
                    },
                    body: JSON.stringify(updatedData)
                });

                // Remove the old document from the original data
                delete this._originalData?.[this.docID];
                // Update the original data after successful update
                this._originalData = { ...updatedData };
            } catch (error) {
                console.error("Error updating document: ", error);
                throw error;
            }
        }

        private async getFileHash(file: File): Promise<string> {
            return new Promise((resolve, reject) => {
                const blobSlice = File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice;
                let fileHash: string;

                if (!file) {
                    console.error("File not provided.");
                    reject(new Error("File not provided."));
                    return;
                }

                // Check if SparkMD5 library is available
                if (typeof window.SparkMD5 === "undefined") {
                    reject(new Error("SparkMD5 library is not available."));
                    return;
                }

                const chunkSize = 2 * 1024 * 1024; // 2MB chunks
                const chunks = Math.ceil(file.size / chunkSize);
                let currentChunk = 0;
                const spark = new SparkMD5.ArrayBuffer();
                const fileReader = new FileReader();

                fileReader.onload = function (e: ProgressEvent<FileReader>) {
                    console.log("Loading chunk...", currentChunk + 1, "of", chunks);
                    spark.append(e.target!.result as ArrayBuffer); // Append array buffer
                    currentChunk++;
                    if (currentChunk < chunks) {
                        loadNext();
                    } else {
                        console.log("Finished loading all chunks. Hashing...");
                        fileHash = spark.end();
                        console.log("File hash:", fileHash);
                        resolve(fileHash);
                    }
                };

                fileReader.onerror = function (e) {
                    console.error("File reading error: ", e);
                    reject(e);
                };

                function loadNext() {
                    const start = currentChunk * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const slice = blobSlice.call(file, start, end);
                    fileReader.readAsArrayBuffer(slice);
                }

                loadNext(); // Start reading the first chunk
            });
        }

        private _validateProperty(property: string, allowEmpty: boolean = false, allowNull: boolean = false): boolean {
            if (!this._hasKey(property)) {
                // Only log if this is unexpected - don't spam console for optional fields
                if (!allowNull && !allowEmpty) {
                    console.error(new Error(`Property "${property}" does not exist.`));
                }
                return false;
            }

            const value = this[property];

            if (value === undefined || value === null) {
                // Only log error if null is not allowed
                if (!allowNull && !allowEmpty) {
                    console.error(new Error(`Property "${property}" is required.`));
                }
                return false;
            }

            if (!allowEmpty && value === "") {
                console.error(new Error(`Property "${property}" must not be empty.`));
                return false;
            }

            return true;
        }

        private _prepare(file: File = null): Object {
            let fileData = {};
            console.log(this._validateProperty("fileName"))
            if (this._validateProperty("fileName", true)) {
                fileData["fileName"] = sanitise(file !== null ? file.name : this?.fileName);
            } else {
                fileData["fileName"] = sanitise(this?.originalFileName);
                // throw new Error("File name is required.");
            }

            if (this.displayName !== undefined) {
                fileData["displayName"] = this.displayName;
            }

            if (this._validateProperty("classification")) {
                fileData["docClassification"] = sanitise(this?.classification);
            } else {
                throw new Error("Classification is required.");
            }

            if (this._validateProperty("comments", true)) {
                fileData["docComments"] = sanitise(this?.comments);
            } else {
                fileData["docComments"] = "";
            }

            if (this._validateProperty("procRef", true)) {
                fileData["docProcRefs"] = this?.procRef;
                fileData["procRef"] = this?.procRef;
            } else {
                fileData["docProcRefs"] = [];
                fileData["procRef"] = [];
            }

            // Debug nextReviewDate handling
            console.log('[SupportingDocs._prepare] nextReviewDate debug:', {
                value: this?.nextReviewDate,
                type: typeof this?.nextReviewDate,
                isDate: this?.nextReviewDate instanceof Date,
                constructor: this?.nextReviewDate?.constructor?.name,
            });
            
            if (this._validateProperty("nextReviewDate")) {
                // Handle various date formats properly
                let dateValue: Date;
                const nrd = this?.nextReviewDate as any;
                if (nrd instanceof Date) {
                    dateValue = nrd;
                } else if (nrd && typeof nrd.toDate === 'function') {
                    dateValue = nrd.toDate();
                } else if (nrd?.__time__) {
                    // Handle { __time__: "ISO string" } format
                    dateValue = new Date(nrd.__time__);
                } else if (typeof nrd === 'string') {
                    dateValue = new Date(nrd);
                } else if (nrd?.seconds) {
                    // Handle Firestore timestamp-like object { seconds, nanoseconds }
                    dateValue = new Date(nrd.seconds * 1000);
                } else {
                    console.warn('[SupportingDocs._prepare] Unknown nextReviewDate format, defaulting to current date');
                    dateValue = new Date();
                }
                fileData["nextReviewDate"] = dateValue;
            } else {
                // Validation failed but try to preserve the date if possible
                if (this?.nextReviewDate) {
                    console.warn('[SupportingDocs._prepare] nextReviewDate validation failed but value exists:', this.nextReviewDate);
                }
                fileData["nextReviewDate"] = new Date();
            }

            if (this._validateProperty("edit")) {
                fileData["edit"] = this?.edit;
            }

            // Always include docID if it exists (for both create and edit operations)
            if (this._validateProperty("docID")) {
                fileData["docID"] = this?.docID;
            }

            if (this._validateProperty("docHash")) {
                fileData["docHash"] = this?.docHash;
            }

            fileData["customerID"] = window.companyID;
            fileData["uploadedBy"] = this?.uploadedBy || auth.currentUser.uid;
            fileData["lastReviewDate"] = this?.lastReviewDate instanceof FirebaseTimestamp
                ? this?.lastReviewDate instanceof firebaseImport.firestore.Timestamp
                    ? this?.lastReviewDate.toDate()
                    : this?.lastReviewDate instanceof Date
                        ? this?.lastReviewDate
                        : new Date()
                : this?.lastReviewDate instanceof Date
                    ? this?.lastReviewDate
                    : new Date();

            fileData["msgPages"] = this?.msgPages !== undefined && this?.msgPages.length > 0 ? [...this?.msgPages.filter(route => route != window.location.hash.slice(2)), window.location.hash.slice(2)] : [window.location.hash.slice(2)];
            fileData["documentType"] = this?.documentType || '';
            fileData["componentId"] = this?.componentId || '';
            fileData["status"] = this?.status || 'current';
            fileData["businessUnit"] = this?.businessUnit || '';
            fileData["linkedFromTaskId"] = this?.linkedFromTaskId || '';

            return fileData;
        }

        private _prepareConfluence(): Object {
            let fileData = {};
            console.log(this._validateProperty("pageName"))
            if (this._validateProperty("pageName")) {
                fileData["pageName"] = this?.pageName;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("pageID")) {
                fileData["pageID"] = this?.pageID;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("confluenceURL")) {
                fileData["confluenceURL"] = this?.pageID;
            } else {
                throw new Error("Page name is required.");
            }

            if (this._validateProperty("classification")) {
                fileData["docClassification"] = sanitise(this?.classification);
            } else {
                throw new Error("Classification is required.");
            }

            if (this._validateProperty("comments", true)) {
                fileData["docComments"] = sanitise(this?.comments);
            } else {
                fileData["docComments"] = "";
            }

            if (this._validateProperty("procRef", true)) {
                fileData["docProcRefs"] = this?.procRef;
                fileData["procRef"] = this?.procRef;
            } else {
                fileData["docProcRefs"] = [];
                fileData["procRef"] = [];
            }

            if (this._validateProperty("nextReviewDate")) {
                fileData["nextReviewDate"] = this?.nextReviewDate instanceof FirebaseTimestamp
                    ? this?.nextReviewDate instanceof firebaseImport.firestore.Timestamp
                        ? this?.nextReviewDate.toDate()
                        : this?.nextReviewDate instanceof Date
                            ? this?.nextReviewDate
                            : new Date()
                    : this?.nextReviewDate instanceof Date
                        ? this?.nextReviewDate
                        : new Date();
            } else {
                fileData["nextReviewDate"] = new Date();
            }

            if (this._validateProperty("edit")) {
                fileData["edit"] = this?.edit;
                fileData["docID"] = this?.docID;
            }

            fileData["customerID"] = window.companyID;
            fileData["uploadedBy"] = this?.uploadedBy || auth.currentUser.uid;
            fileData["lastReviewDate"] = this?.lastReviewDate instanceof FirebaseTimestamp
                ? this?.lastReviewDate instanceof firebaseImport.firestore.Timestamp
                    ? this?.lastReviewDate.toDate()
                    : this?.lastReviewDate instanceof Date
                        ? this?.lastReviewDate
                        : new Date()
                : this?.lastReviewDate instanceof Date
                    ? this?.lastReviewDate
                    : new Date();
            fileData["msgPages"] = this?.msgPages !== undefined && this?.msgPages.length > 0 ? [...this?.msgPages.filter(route => route != window.location.hash.slice(2)), window.location.hash.slice(2)] : [window.location.hash.slice(2)];
            fileData["documentType"] = this?.documentType || '';
            fileData["componentId"] = this?.componentId || '';
            fileData["status"] = this?.status || 'current';

            return fileData;
        }

        private async _handleConfluenceFiles() {
            try {
                const confluenceData = this._prepareConfluence();
                const isEdit = this?.edit === true;
                
                if (isEdit && this?.docID) {
                    // Update existing document
                    await deiterateAPI.PUT(`/v1/documents/${this.docID}`, {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: {
                            'X-Tenancy-Id': window.companyID,
                            'Company-ID': window.companyID,
                        },
                        body: JSON.stringify({
                            ...confluenceData,
                            docID: this.docID,
                        })
                    });
                } else {
                    // Create new document
                    await deiterateAPI.POST('/v1/documents', {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: {
                            'X-Tenancy-Id': window.companyID,
                            'Company-ID': window.companyID,
                        },
                        body: JSON.stringify({
                            ...confluenceData,
                            docID: this.docID,
                        })
                    });
                }
            } catch (error) {
                throw new Error("Error handling Confluence files: ", error);
            }
        }

        /* Upload File to Firebase Storage and Firestore */
        public async upload(
            type: String = "file",
            file: File | ConfluencePage | object | null = null,
            newDocID: string | number | null = null,
            createMode: boolean = false,
        ): Promise<void> {

            if (type === "confluence") {
                return this._handleConfluenceFiles();
            }

            if (this._validateProperty("docID") && (file instanceof File || file === null) === true) {
                return this.replace(file instanceof File ? file : null, newDocID, createMode);
            } else {
                console.log("No DocID");
            }

        }

        /*
        * Replace file in firebase storage
        * and update the document in Firestore.
        */
        public async replace(
            file: File | null,
            newDocID: string | number | null = null,
            createMode: boolean = false,
        ): Promise<void> {
            let docID: string | number | null = this?.docID ? this?.docID : null;

            if (newDocID !== null) {
                docID = newDocID;
            }

            if (!docID) {
                throw new Error("Cannot replace file without a docID.");
            }

            if (file !== null) {
                let fileUploadReference = storageRef.child(
                    `/customers/${window.companyID}/keyDocuments/otherDocs/uploaded/${this?.docID}/${sanitise(file?.name)}`
                )

                try {
                    await fileUploadReference.put(file);
                } catch (error) {
                    throw new Error(`Failed to replace ${this?.fileName}.`, error)
                }

                const fileHash = await this.getFileHash(file);

                if (fileHash === undefined || fileHash === null || fileHash === "") {
                    throw new Error("Failed to get file hash or invalid file type.");
                }

                this.docHash = fileHash;
            }

            if (!createMode) {
                const fileData = this._prepare(file);
                const isEdit = this?.edit === true;
                
                if (isEdit && this?.docID) {
                    // Update existing document
                    return await deiterateAPI.PUT(`/v1/documents/${this.docID}`, {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: {
                            'X-Tenancy-Id': window.companyID,
                            'Company-ID': window.companyID,
                        },
                        body: JSON.stringify({
                            ...fileData,
                            docID: this.docID,
                        })
                    }).then(response => {
                        return response;
                    });
                } else {
                    // Create new document
                    return await deiterateAPI.POST('/v1/documents', {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: {
                            'X-Tenancy-Id': window.companyID,
                            'Company-ID': window.companyID,
                        },
                        body: JSON.stringify({
                            ...fileData,
                            docID: this.docID,
                        })
                    }).then(response => {
                        return response;
                    });
                }
            } else {
                return new Promise<void>((resolve, reject) => {
                    resolve();
                })
            }
        }

        /*
        * Download the document file
        * Handles both file downloads and Confluence URL opening
        * Supports multiple storage locations with fallback
        */
        public async download(): Promise<void> {
            return new Promise<void>(async (resolve, reject) => {
                if (!this.docID) {
                    reject(new Error("Document ID is required for download"));
                    return;
                }

                // For Confluence documents, just open the URL
                if (this.confluenceURL && this.confluenceURL.trim() !== '') {
                    window.open(this.confluenceURL, '_blank');
                    resolve();
                    return;
                }

                // Must have a filename for file-based documents
                if (!this.fileName || this.fileName.trim() === '') {
                    reject(new Error("Filename is required for file download"));
                    return;
                }

                const companyID = this.companyID === undefined ? window.companyID : this.companyID;

                // Build list of possible storage paths to try
                const pathsToTry: string[] = [];

                // 1. If filePath property exists, use it first (remove leading slash if present)
                if ((this as any).filePath) {
                    const filePath = (this as any).filePath.startsWith('/') 
                        ? (this as any).filePath.substring(1) 
                        : (this as any).filePath;
                    pathsToTry.push(filePath);
                }

                // 2. Standard supporting docs path
                pathsToTry.push(`customers/${companyID}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`);

                // 3. Policies storage path (used by policy admin uploads)
                pathsToTry.push(`customers/${companyID}/policies/${this.docID}/${this.fileName}`);

                console.log('[Document.download] Attempting download from paths:', pathsToTry);

                // Try each path until one succeeds
                for (let i = 0; i < pathsToTry.length; i++) {
                    const storagePath = pathsToTry[i];
                    console.log(`[Document.download] Trying path ${i + 1}/${pathsToTry.length}:`, storagePath);

                    try {
                        const result = await downloadFromSDK(
                            firebaseConfig,
                            storageRef,
                            'download',
                            null,
                            this.fileName,
                            storagePath
                        );
                        
                        // downloadFromSDK returns null if file not found (doesn't throw)
                        if (result === null) {
                            console.log(`[Document.download] File not found at path ${i + 1} (null result), trying next...`);
                            if (i < pathsToTry.length - 1) {
                                continue;
                            }
                            // Last path also returned null
                            const notFoundError = new Error('File not found in storage');
                            (notFoundError as any).code = 'storage/object-not-found';
                            reject(notFoundError);
                            return;
                        }
                        
                        console.log('[Document.download] Success with path:', storagePath);
                        resolve();
                        return;
                    } catch (error: any) {
                        const isNotFound = error?.code === 'storage/object-not-found' || 
                            error?.message?.includes('does not exist') ||
                            error?.message?.includes('404');
                        
                        if (isNotFound && i < pathsToTry.length - 1) {
                            // File not found at this path, try next
                            console.log(`[Document.download] File not found at path ${i + 1}, trying next...`);
                            continue;
                        }
                        
                        // Either not a "not found" error, or we've exhausted all paths
                        console.error("[Document.download] Error during download:", error);
                        reject(error);
                        return;
                    }
                }

                // Should not reach here, but just in case
                reject(new Error("Failed to download file from any known location"));
            });
        }

        /*
        * Delete File
        */
        /**
         * Creates a revision of the current document before deletion via API
         * @private
         */
        private async _createRevision(): Promise<string> {
            if (!this.docID) {
                throw new Error("Cannot create revision without a docID.");
            }

            try {
                const companyID = this.companyID || window.companyID;
                
                const res = await deiterateAPI.POST(`/v1/documents/${this.docID}/revision`, {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: {
                        'X-Tenancy-Id': companyID,
                        'Company-ID': companyID,
                        'X-User-Id': auth.currentUser?.uid || 'system',
                    }
                });

                if (!res.revisionId) {
                    throw new Error("Failed to create revision - no revision ID returned");
                }

                // Copy the file to a backup location in storage if it exists
                if (this.fileName && !this.confluenceURL) {
                    const sourceRef = storageRef.child(`/customers/${companyID}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`);
                    const backupRef = storageRef.child(`/customers/${companyID}/documentRevisions/${res.revisionId}/${this.fileName}`);

                    try {
                        const fileUrl = await sourceRef.getDownloadURL();
                        const response = await fetch(fileUrl);
                        const blob = await response.blob();
                        await backupRef.put(blob);
                    } catch (error) {
                        console.warn("Could not backup file to revision:", error);
                        // Continue with deletion even if backup fails
                    }
                }

                return res.revisionId;
            } catch (error) {
                console.error("Failed to create document revision:", error);
                throw new Error("Failed to create document revision before deletion.");
            }
        }

        /**
         * Deletes the document via API (which creates revision automatically)
         */
        public async delete(): Promise<string> {
            try {
                if (!this._validateProperty("docID")) {
                    throw new Error("Cannot delete file without a docID.");
                }

                const companyID = this.companyID || window.companyID;
                const storageReference = this.fileName
                    ? storageRef.child(`/customers/${companyID}/keyDocuments/otherDocs/uploaded/${this.docID}/${this.fileName}`)
                    : null;

                // Delete via API - this creates revision automatically
                const res = await deiterateAPI.DELETE(`/v1/documents/${this.docID}`, {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: {
                        'X-Tenancy-Id': companyID,
                        'Company-ID': companyID,
                        'X-User-Id': auth.currentUser?.uid || 'system',
                    },
                    body: JSON.stringify({ createRevision: true })
                });

                if (!res.success) {
                    throw new Error(`Failed to delete document: ${res.error || 'Unknown error'}`);
                }

                // Delete the file from storage if it exists
                if (storageReference) {
                    await storageReference.delete().catch(error => {
                        console.warn(`Failed to delete ${this.fileName} from storage:`, error);
                        // Don't throw here as the document was already deleted from API
                    });
                }

                return res.revisionId || '';
            } catch (error) {
                console.error("Failed to delete document:", error);
                throw new Error(`Failed to delete file: ${error.message}`);
            }
        }

        /**
         * Recovers a deleted document from a revision via API
         * @static
         * @param revisionId The ID of the revision to recover
         * @param newDocId Optional new document ID (if not provided, will use original)
         */
        public static async recover(revisionId: string, newDocId: string | null = null): Promise<Document> {
            try {
                const companyID = window.companyID;
                
                const res = await deiterateAPI.POST(`/v1/documents/recover/${revisionId}`, {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: {
                        'X-Tenancy-Id': companyID,
                        'Company-ID': companyID,
                        'X-User-Id': auth.currentUser?.uid || 'system',
                    },
                    body: JSON.stringify({ newDocId })
                });

                if (!res.success) {
                    throw new Error(res.error || "Failed to recover document");
                }

                const documentData = res.documentData;
                const docId = res.docId;

                // Restore the file from backup storage if it exists
                if (documentData.fileName && !documentData.confluenceURL) {
                    const sourceRef = storageRef.child(`/customers/${companyID}/documentRevisions/${revisionId}/${documentData.fileName}`);
                    const targetRef = storageRef.child(`/customers/${companyID}/keyDocuments/otherDocs/uploaded/${docId}/${documentData.fileName}`);

                    try {
                        const fileUrl = await sourceRef.getDownloadURL();
                        const response = await fetch(fileUrl);
                        const blob = await response.blob();
                        await targetRef.put(blob);
                    } catch (error) {
                        console.warn("Could not restore file from revision:", error);
                        // Continue with recovery even if file restore fails
                    }
                }

                return new Document({
                    ...documentData,
                    docID: docId,
                    companyID
                });
            } catch (error) {
                console.error("Failed to recover document:", error);
                throw new Error(`Failed to recover document: ${error.message}`);
            }
        }

        /**
         * Helper method to compare two objects.
         */
        private _isEqual(obj1: any, obj2: any): boolean {
            return JSON.stringify(obj1) === JSON.stringify(obj2);
        }

        /**
         * Creates a Document instance from Firestore data.
         */
        public static fromData(documentData: IDocumentData): Document {
            return new Document(documentData);
        }

        /**
         * Creates a Document instance from a Firestore snapshot.
         */
        public static fromSnapshot(
            snapshot: firebaseImport.firestore.DocumentSnapshot
        ): Document {
            if (!snapshot.exists) {
                return null;
            }
            return new Document(snapshot.data() as IDocumentData);
        }

        /**
         * Get the next available document ID
         * @param companyID Optional company ID, defaults to current company
         * @returns Promise resolving to the next available document ID as a number
         */
        public static async getNextAvailableDocID(companyID: CompanyID | null = null): Promise<number> {
            // Set default company ID if not provided
            if (companyID === null || companyID === undefined) {
                companyID = window.companyID;
            }

            let highestID = 0;

            try {
                // Query Firestore directly to get the raw document data
                const documentsReference = db
                    .collection("customers")
                    .doc(companyID)
                    .collection("product")
                    .doc("policyPackages")
                    .collection("ISO27001keyDocuments")
                    .doc("otherDocs");

                const doc = await documentsReference.get();

                if (doc.exists) {
                    const data = doc.data() as any;
                    const files = data.uploaded || {};

                    // Also check the doc_count value, which tracks the total documents
                    if (data.doc_count !== undefined && data.doc_count !== null) {
                        const docCount = typeof data.doc_count === 'number'
                            ? data.doc_count
                            : parseInt(String(data.doc_count), 10);

                        if (!isNaN(docCount) && docCount > highestID) {
                            console.log(`[SupportingDocs] Using doc_count as minimum: ${docCount}`);
                            highestID = docCount;
                        }
                    }

                    // Check numeric keys in the uploaded object
                    for (const key in files) {
                        if (files.hasOwnProperty(key)) {
                            const keyAsNumber = parseInt(key, 10);
                            if (!isNaN(keyAsNumber) && keyAsNumber > highestID) {
                                highestID = keyAsNumber;
                            }

                            // Also check the docID field inside each document
                            const docIdValue = files[key].docID;
                            if (docIdValue !== undefined && docIdValue !== null) {
                                const docIdAsNumber = typeof docIdValue === 'number'
                                    ? docIdValue
                                    : parseInt(String(docIdValue), 10);

                                if (!isNaN(docIdAsNumber) && docIdAsNumber > highestID) {
                                    highestID = docIdAsNumber;
                                }
                            }
                        }
                    }
                }

                // Always use highest ID + 1 to ensure no conflicts
                const nextID = highestID + 1;
                console.log(`[SupportingDocs] Using next ID: ${nextID} (highest found: ${highestID})`);
                return nextID;
            } catch (error) {
                console.error("Error getting highest document ID: ", error);
                // If there's an error, return a safe default (at least 1)
                return Math.max(1, highestID + 1);
            }
        }
    }
}